import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Organization, v1OrganizationsApi } from '../api'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Loader2, CheckCircle2 } from 'lucide-react'
import { toast } from 'sonner'

interface AddOrganizationUserDialogProps {
  organization: Organization
  onClose: () => void
}

export function AddOrganizationUserDialog({ organization, onClose }: AddOrganizationUserDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())

  // Use organization's domain for migration
  const orgDomain = organization.domain || ''

  // Fetch users by domain
  const { data: domainUsers, isLoading: isLoadingDomainUsers } = useQuery({
    queryKey: ['users-by-domain', organization._id, orgDomain],
    queryFn: () => v1OrganizationsApi.getUsersByDomain(
      organization._id,
      orgDomain,
      organization._id, // Exclude current org
      500
    ),
    enabled: orgDomain.length > 0,
  })

  const handleTransfer = async () => {
    if (selectedUsers.size === 0) {
      toast.error('No users selected', {
        description: 'Please select at least one user to transfer'
      })
      return
    }

    setIsSubmitting(true)

    try {
      const response = await v1OrganizationsApi.bulkTransferUsers(
        organization._id,
        {
          user_ids: Array.from(selectedUsers)
        }
      )

      const { summary } = response

      toast.success('Migration completed', {
        description: `${summary.succeeded} user${summary.succeeded !== 1 ? 's' : ''} transferred successfully. ${summary.totalAgentsTransferred} agents, ${summary.totalPromptsTransferred} prompts, ${summary.totalAclEntriesUpdated} ACL entries migrated.`
      })

      // Show failed users if any
      if (response.failed.length > 0) {
        toast.error('Some transfers failed', {
          description: response.failed.map(f => f.email || f.user_id).join(', ')
        })
      }

      setSelectedUsers(new Set())
      onClose()
    } catch (error: any) {
      toast.error('Migration failed', {
        description: error?.response?.data?.message || 'Failed to transfer users'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUserToggle = (userId: string) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev)
      if (newSet.has(userId)) {
        newSet.delete(userId)
      } else {
        newSet.add(userId)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    if (domainUsers?.users) {
      if (selectedUsers.size === domainUsers.users.length) {
        setSelectedUsers(new Set())
      } else {
        setSelectedUsers(new Set(domainUsers.users.map(u => u._id)))
      }
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedUsers(new Set())
      onClose()
    }
  }

  const availableUsers = domainUsers?.users || []
  const allSelected = availableUsers.length > 0 && selectedUsers.size === availableUsers.length

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Migrate Users to Organization</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!orgDomain ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              Migration requires the organization to have a domain configured.
              Please edit the organization to add a domain first.
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Showing users with domain <span className="font-medium">{orgDomain}</span> from other organizations.
                Selected users will be transferred with all their agents, prompts, and permissions.
              </p>

              {/* Users List */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Available Users ({availableUsers.length})</Label>
                  {availableUsers.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAll}
                    >
                      {allSelected ? 'Deselect All' : 'Select All'}
                    </Button>
                  )}
                </div>

                {isLoadingDomainUsers ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : availableUsers.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    No users found with domain "{orgDomain}" in other organizations
                  </div>
                ) : (
                  <div className="border rounded-md max-h-[300px] overflow-y-auto">
                    {availableUsers.map((user) => (
                      <div
                        key={user._id}
                        className="flex items-center space-x-3 p-3 hover:bg-muted/50 border-b last:border-b-0"
                      >
                        <Checkbox
                          checked={selectedUsers.has(user._id)}
                          onCheckedChange={() => handleUserToggle(user._id)}
                          disabled={isSubmitting}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{user.email}</p>
                          {user.name && (
                            <p className="text-xs text-muted-foreground truncate">{user.name}</p>
                          )}
                          {user.orgId && (
                            <p className="text-xs text-muted-foreground">
                              Current org: {user.orgId}
                            </p>
                          )}
                        </div>
                        {selectedUsers.has(user._id) && (
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {selectedUsers.size > 0 && (
                  <div className="text-sm text-muted-foreground">
                    {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
                  </div>
                )}
              </div>
            </>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleTransfer}
              disabled={isSubmitting || selectedUsers.size === 0 || !orgDomain}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Migrating...
                </>
              ) : (
                `Migrate ${selectedUsers.size} User${selectedUsers.size !== 1 ? 's' : ''}`
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
